import React, { useCallback, useEffect, useState } from "react";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import { NavLink } from "react-router-dom";

import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster";
import user_service from "../service/user_service";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const EMPTY_FORM = {
  id: "",
  title: "",
  body: "",
  sendAt: "",
  visibleInArchive: true,
  includeBirthdaysAnniversaries: false,
  contactType: [],
  contactGroup: [],
  officeGroup: [],
};

const emptyEditor = () => EditorState.createEmpty();

const editorFromHtml = (html = "") => {
  if (!html) return emptyEditor();
  const blocks = htmlToDraft(html);
  if (!blocks) return emptyEditor();
  const content = ContentState.createFromBlockArray(
    blocks.contentBlocks,
    blocks.entityMap
  );
  return EditorState.createWithContent(content);
};

const dateTimeInputValue = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getApiError = (error, fallback) =>
  error?.response?.data?.message ||
  (typeof error?.response?.data === "string" ? error.response.data : "") ||
  error?.message ||
  fallback;

const recipientKey = (item) =>
  String(item?._id || item?.email || item?.name || item?.groupDetails?.group_name);

const recipientLabel = (item, type) => {
  if (type === "contactGroup") {
    return item?.groupDetails?.group_name || item?.group_name || "Group";
  }
  if (type === "officeGroup") {
    return [item?.tag, item?.name].filter(Boolean).join(" ") || "Office";
  }
  return (
    [item?.firstName, item?.lastName].filter(Boolean).join(" ") ||
    item?.email ||
    "Associate"
  );
};

const associatePayload = (associate = {}) => ({
  _id: associate._id || "",
  email: associate.email || "",
  firstName: associate.firstName || "",
  lastName: associate.lastName || "",
  image: associate.image || "",
  active_office: associate.active_office || "",
});

const memberPayload = (associate = {}) => ({
  _id: associate._id || "",
  email: associate.email || "",
  firstName: associate.firstName || "",
});

const groupPayload = (group = {}) => ({
  _id: group._id || "",
  groupDetails: group.groupDetails || {},
  group_member: Array.isArray(group.group_member)
    ? group.group_member.map(memberPayload)
    : [],
});

const officePayload = (office = {}) => ({
  _id: office._id || "",
  name: office.name || "",
  tag: office.tag || "",
  associates: Array.isArray(office.associates)
    ? office.associates.map(memberPayload)
    : [],
});

function NewsContent() {
  const [issues, setIssues] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editorState, setEditorState] = useState(emptyEditor);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({
    types: "",
    isShow: false,
    toasterBody: "",
    message: "",
  });

  const [recipientMode, setRecipientMode] = useState("");
  const [associateQuery, setAssociateQuery] = useState("");
  const [associateResults, setAssociateResults] = useState([]);
  const [groups, setGroups] = useState([]);
  const [offices, setOffices] = useState([]);

  const showToast = useCallback((message, toasterBody = "", types = "") => {
    setToaster({ message, toasterBody, types, isShow: true });
    window.setTimeout(
      () => setToaster((current) => ({ ...current, isShow: false })),
      2500
    );
  }, []);

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      setApiError("");
      const response = await user_service.newslettersGet();
      setIssues(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      setApiError(getApiError(error, "Unable to load newsletters."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecipientOptions = useCallback(async () => {
    try {
      const [groupResponse, officeResponse] = await Promise.all([
        user_service.contactGroupMemberGetAll(),
        user_service.organizationTreeGet(),
      ]);
      setGroups(
        Array.isArray(groupResponse?.data?.data) ? groupResponse.data.data : []
      );
      const officeData = officeResponse?.data?.data || officeResponse?.data || [];
      setOffices(Array.isArray(officeData) ? officeData : []);
    } catch (error) {
      setApiError(getApiError(error, "Unable to load recipient options."));
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadIssues();
    loadRecipientOptions();
  }, [loadIssues, loadRecipientOptions]);

  useEffect(() => {
    if (recipientMode !== "associate" || associateQuery.trim().length < 2) {
      setAssociateResults([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      try {
        const response = await user_service.SearchContactfilterAssociate(
          1,
          "associate",
          "active",
          associateQuery.trim()
        );
        setAssociateResults(
          Array.isArray(response?.data?.data) ? response.data.data : []
        );
      } catch (error) {
        setApiError(getApiError(error, "Unable to search associates."));
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [associateQuery, recipientMode]);

  const resetComposer = () => {
    setForm(EMPTY_FORM);
    setEditorState(emptyEditor());
    setErrors({});
    setApiError("");
    setRecipientMode("");
    setAssociateQuery("");
    setAssociateResults([]);
  };

  const editIssue = async (id) => {
    try {
      setLoading(true);
      setApiError("");
      const response = await user_service.newsletterGetById(id);
      const issue = response?.data;
      setForm({
        id: issue?._id || "",
        title: issue?.title || "",
        body: issue?.body || "",
        sendAt: dateTimeInputValue(issue?.sendAt),
        visibleInArchive: issue?.visibleInArchive !== false,
        includeBirthdaysAnniversaries: Boolean(
          issue?.includeBirthdaysAnniversaries
        ),
        contactType: Array.isArray(issue?.contactType) ? issue.contactType : [],
        contactGroup: Array.isArray(issue?.contactGroup)
          ? issue.contactGroup
          : [],
        officeGroup: Array.isArray(issue?.officeGroup) ? issue.officeGroup : [],
      });
      setEditorState(editorFromHtml(issue?.body));
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setApiError(getApiError(error, "Unable to open this newsletter."));
    } finally {
      setLoading(false);
    }
  };

  const deleteIssue = async (issue) => {
    if (!window.confirm(`Delete "${issue.title}"? This cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      setApiError("");
      await user_service.newsletterDelete(issue._id);
      if (form.id === issue._id) resetComposer();
      await loadIssues();
      showToast("Newsletter deleted");
    } catch (error) {
      setApiError(getApiError(error, "Unable to delete this newsletter."));
    } finally {
      setLoading(false);
    }
  };

  const toggleArchiveVisibility = async (issue) => {
    try {
      setLoading(true);
      setApiError("");
      await user_service.newsletterSetVisibility(
        issue._id,
        !issue.visibleInArchive
      );
      await loadIssues();
      showToast(
        issue.visibleInArchive
          ? "Newsletter hidden from archive"
          : "Newsletter shown in archive"
      );
    } catch (error) {
      setApiError(
        getApiError(error, "Unable to update archive visibility.")
      );
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = (type, item) => {
    if (!item) return;
    setForm((current) => {
      if (current[type].some((selected) => recipientKey(selected) === recipientKey(item))) {
        return current;
      }
      return { ...current, [type]: [...current[type], item] };
    });
    if (type === "contactType") {
      setAssociateQuery("");
      setAssociateResults([]);
    }
  };

  const removeRecipient = (type, item) => {
    setForm((current) => ({
      ...current,
      [type]: current[type].filter(
        (selected) => recipientKey(selected) !== recipientKey(item)
      ),
    }));
  };

  const handleEditorChange = (nextEditorState) => {
    const body = draftToHtml(convertToRaw(nextEditorState.getCurrentContent()));
    setEditorState(nextEditorState);
    setForm((current) => ({ ...current, body }));
    if (errors.body) setErrors((current) => ({ ...current, body: "" }));
  };

  const validate = (action) => {
    const nextErrors = {};
    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!editorState.getCurrentContent().hasText()) {
      nextErrors.body = "Newsletter content is required.";
    }
    if (action === "scheduled" && form.sendAt) {
      const sendDate = new Date(form.sendAt);
      if (Number.isNaN(sendDate.getTime())) {
        nextErrors.sendAt = "Enter a valid send date and time.";
      } else if (sendDate.getTime() <= Date.now()) {
        nextErrors.sendAt =
          "Choose a future time, or clear the field to publish now.";
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const save = async (action) => {
    if (!validate(action)) return;

    const publishingNow = action === "scheduled" && !form.sendAt;
    const payload = {
      title: form.title.trim(),
      body: form.body,
      status: action,
      sendAt:
        action === "scheduled"
          ? publishingNow
            ? new Date().toISOString()
            : new Date(form.sendAt).toISOString()
          : null,
      visibleInArchive: form.visibleInArchive,
      includeBirthdaysAnniversaries: form.includeBirthdaysAnniversaries,
      contactType: form.contactType.map(associatePayload),
      contactGroup: form.contactGroup.map(groupPayload),
      officeGroup: form.officeGroup.map(officePayload),
    };

    try {
      setLoading(true);
      setApiError("");
      if (form.id) {
        await user_service.newsletterUpdate(form.id, payload);
      } else {
        await user_service.newsletterCreate(payload);
      }
      await loadIssues();
      resetComposer();
      showToast(
        action === "draft"
          ? "Draft saved"
          : publishingNow
            ? "Newsletter queued for publishing"
            : "Newsletter scheduled"
      );
    } catch (error) {
      setApiError(getApiError(error, "Unable to save this newsletter."));
    } finally {
      setLoading(false);
    }
  };

  const selectedRecipientCount =
    form.contactType.length + form.contactGroup.length + form.officeGroup.length;

  return (
    <div className="bg-secondary float-left w-100 pt-4 pb-4">
      <Loader isActive={loading} />
      {toaster.isShow && <Toaster {...toaster} />}
      <main className="page-wrapper">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h3 className="text-white mb-0">Newsletters</h3>
          <button className="btn btn-light" type="button" onClick={resetComposer}>
            Create Next Newsletter
          </button>
        </div>

        {apiError && (
          <div className="alert alert-danger" role="alert">
            {apiError}
          </div>
        )}

        <section className="bg-light border rounded-3 p-3 mb-4">
          <h4 className="mb-3">
            {form.id ? "Edit Newsletter" : "Compose Newsletter"}
          </h4>

          <div className="row">
            <div className="col-lg-8">
              <div className="mb-3">
                <label className="form-label" htmlFor="newsletter-title">
                  Issue title
                </label>
                <input
                  id="newsletter-title"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={form.title}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }));
                    if (errors.title) {
                      setErrors((current) => ({ ...current, title: "" }));
                    }
                  }}
                  placeholder="September newsletter"
                />
                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Content</label>
                <div
                  className={`border rounded bg-white ${
                    errors.body ? "border-danger" : ""
                  }`}
                >
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={handleEditorChange}
                    toolbar={{
                      options: [
                        "inline",
                        "blockType",
                        "fontSize",
                        "list",
                        "textAlign",
                        "link",
                        "image",
                        "history",
                      ],
                      image: {
                        urlEnabled: true,
                        uploadEnabled: true,
                        alignmentEnabled: true,
                        previewImage: true,
                        inputAccept:
                          "image/gif,image/jpeg,image/jpg,image/png,image/svg",
                        uploadCallback: (file) =>
                          new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (event) =>
                              resolve({ data: { link: event.target.result } });
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                          }),
                      },
                    }}
                    wrapperClassName="demo-wrapper"
                    editorClassName="demo-editor px-2"
                    editorStyle={{ minHeight: "320px" }}
                  />
                </div>
                {errors.body && (
                  <div className="text-danger small mt-1">{errors.body}</div>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="mb-3">
                <label className="form-label" htmlFor="newsletter-recipient-type">
                  Add recipients
                </label>
                <select
                  id="newsletter-recipient-type"
                  className="form-select"
                  value={recipientMode}
                  onChange={(event) => setRecipientMode(event.target.value)}
                >
                  <option value="">Choose recipient type</option>
                  <option value="associate">Associate</option>
                  <option value="group">Group/Team</option>
                  <option value="office">Office</option>
                </select>
              </div>

              {recipientMode === "associate" && (
                <div className="mb-3">
                  <input
                    className="form-control"
                    value={associateQuery}
                    onChange={(event) => setAssociateQuery(event.target.value)}
                    placeholder="Search active associates"
                    aria-label="Search active associates"
                  />
                  {associateResults.length > 0 && (
                    <div className="list-group mt-1">
                      {associateResults.map((associate) => (
                        <button
                          className="list-group-item list-group-item-action"
                          type="button"
                          key={recipientKey(associate)}
                          onClick={() => addRecipient("contactType", associate)}
                        >
                          {recipientLabel(associate, "contactType")}
                          {associate.email ? ` — ${associate.email}` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {recipientMode === "group" && (
                <div className="list-group mb-3">
                  {groups.length ? (
                    groups.map((group) => (
                      <button
                        className="list-group-item list-group-item-action"
                        type="button"
                        key={recipientKey(group)}
                        onClick={() => addRecipient("contactGroup", group)}
                      >
                        {recipientLabel(group, "contactGroup")}
                      </button>
                    ))
                  ) : (
                    <div className="text-muted small">No groups found.</div>
                  )}
                </div>
              )}

              {recipientMode === "office" && (
                <div className="list-group mb-3">
                  {offices.length ? (
                    offices.map((office) => (
                      <button
                        className="list-group-item list-group-item-action"
                        type="button"
                        key={recipientKey(office)}
                        onClick={() => addRecipient("officeGroup", office)}
                      >
                        {recipientLabel(office, "officeGroup")}
                      </button>
                    ))
                  ) : (
                    <div className="text-muted small">No offices found.</div>
                  )}
                </div>
              )}

              <div className="border rounded p-2 mb-3">
                <strong>Selected recipients</strong>
                {selectedRecipientCount === 0 ? (
                  <p className="text-muted small mb-0 mt-1">
                    None selected. This newsletter will go to all active
                    associates.
                  </p>
                ) : (
                  ["contactType", "contactGroup", "officeGroup"].map((type) =>
                    form[type].map((item) => (
                      <div
                        className="d-flex justify-content-between align-items-center mt-2"
                        key={`${type}-${recipientKey(item)}`}
                      >
                        <span>{recipientLabel(item, type)}</span>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          type="button"
                          onClick={() => removeRecipient(type, item)}
                          aria-label={`Remove ${recipientLabel(item, type)}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )
                )}
              </div>

              <div className="form-check mb-2">
                <input
                  id="newsletter-dates"
                  className="form-check-input"
                  type="checkbox"
                  checked={form.includeBirthdaysAnniversaries}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      includeBirthdaysAnniversaries: event.target.checked,
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="newsletter-dates">
                  Include birthdays and anniversaries
                </label>
              </div>

              <div className="form-check mb-3">
                <input
                  id="newsletter-archive"
                  className="form-check-input"
                  type="checkbox"
                  checked={form.visibleInArchive}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      visibleInArchive: event.target.checked,
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="newsletter-archive">
                  Show in archive after sending
                </label>
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="newsletter-send-at">
                  Schedule date and time
                </label>
                <input
                  id="newsletter-send-at"
                  className={`form-control ${errors.sendAt ? "is-invalid" : ""}`}
                  type="datetime-local"
                  value={form.sendAt}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      sendAt: event.target.value,
                    }))
                  }
                />
                <div className="form-text">
                  Leave blank to publish immediately.
                </div>
                {errors.sendAt && (
                  <div className="invalid-feedback">{errors.sendAt}</div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap justify-content-end gap-2">
            <NavLink className="btn btn-secondary" to="/control-panel">
              Cancel
            </NavLink>
            {form.id && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={resetComposer}
              >
                Discard Changes
              </button>
            )}
            <button
              className="btn btn-outline-primary"
              type="button"
              disabled={loading}
              onClick={() => save("draft")}
            >
              Save Draft
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={loading}
              onClick={() => save("scheduled")}
            >
              {form.sendAt ? "Schedule" : "Publish Now"}
            </button>
          </div>
        </section>

        <section className="bg-light border rounded-3 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Newsletter Issues</h4>
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={loadIssues}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Issue</th>
                  <th>Status</th>
                  <th>Send date</th>
                  <th>Archive</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issues.length ? (
                  issues.map((issue) => {
                    const sent = issue.status === "sent";
                    return (
                      <tr key={issue._id}>
                        <td>
                          <strong>{issue.title}</strong>
                          <div className="text-muted small">
                            Updated{" "}
                            {issue.updatedAt
                              ? new Date(issue.updatedAt).toLocaleString()
                              : "—"}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              sent
                                ? "bg-success"
                                : issue.status === "scheduled"
                                  ? "bg-primary"
                                  : "bg-secondary"
                            }`}
                          >
                            {issue.status}
                          </span>
                        </td>
                        <td>
                          {issue.sentAt || issue.sendAt
                            ? new Date(issue.sentAt || issue.sendAt).toLocaleString()
                            : "Not scheduled"}
                        </td>
                        <td>{issue.visibleInArchive ? "Yes" : "No"}</td>
                        <td className="text-end">
                          {sent && (
                            <button
                              className="btn btn-sm btn-outline-secondary me-2"
                              type="button"
                              onClick={() => toggleArchiveVisibility(issue)}
                            >
                              {issue.visibleInArchive
                                ? "Hide from Archive"
                                : "Show in Archive"}
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            type="button"
                            disabled={sent}
                            title={sent ? "Sent newsletters cannot be edited" : ""}
                            onClick={() => editIssue(issue._id)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            disabled={sent}
                            title={sent ? "Sent newsletters cannot be deleted" : ""}
                            onClick={() => deleteIssue(issue)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="text-center text-muted py-4" colSpan="5">
                      No newsletter issues yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default NewsContent;
